import { DocumentNode, useMutation } from '@apollo/client';
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import {
  AlertType,
  IPurchaseInvoice,
  IPurchaseInvoicePostMutationResult,
  IPurchaseInvoicePostMutationVariables,
  ISalesInvoice,
  ISalesInvoiceInput,
  ISalesInvoicePostMutationResult,
  ISalesInvoicePostMutationVariables,
  Maybe,
} from '../graphql/schema/p4types';
import { useDispatch } from 'react-redux';
import ConnectedFormContext from '../components/Context/ConnectedFormContext';
import { alertAction, handleComponentQueryError } from '../utils/parseErrorResponse';
import normalizeVariables from '../graphql/schema/normalizeVariables';
import hasValidationErrors from '../components/connectedForm/utils/hasValidationErrors';
import getClientMutationId from '../utils/getClientMutationId';
import { change } from '../components/connectedForm/utils/actions';
import { usePerformSave } from '../components/connectedForm/Hooks/usePerformSave';
import { usePostConfirmationPrompt } from '../utils/SalesLedgerHooks';
import { POST_OPTIONS, useSelectButtonProps } from '../components/SelectButton';
import { IFormAction } from '../@types/entityTypes';
import { SalesInvoiceFormAction } from './salesLedger/salesInvoice/SalesInvoiceAction';
import { PurchaseInvoiceFormAction } from './supplier/PurchaseInvoiceAction';
import { serviceActions } from '../state/modules/service';
import { Analytics } from '../mixpanel';
import { FetchResult } from '@apollo/client/link/core/types';

const canBePosted = (invoice: ISalesInvoice | IPurchaseInvoice) => {
  if (invoice && !(invoice as ISalesInvoice).archived && invoice.documentStatus) {
    return invoice.documentStatus === 'DRAFT' || invoice.documentStatus === 'RELEASED';
  }
  return false;
};

/**
 * Shared post logic as this works near-enough the same for Sales/Purchase invoices.
 * @param postMutation          Mutation for the post (Expecting SalesInvoicePostMutation or PurchaseInvoicePostMutation)
 * @param type                  Whether this is a Sales or Purchase invoice post.
 * @param setPOMatchSlideIndex  Setter for PO match grid - to reset selection slide
 */
export function useInvoicePostAction(
  postMutation: DocumentNode,
  type: 'Sales' | 'Purchase' = 'Sales',
  setPOMatchSlideIndex?: (index: number) => void
) {
  const [postConfirmationState, setPostConfirmationState] =
    useState<Maybe<{ meta: any; title: string; message: string }>>(null);
  const [postInProgress, setPostInProgress] = useState(false);
  const [variables, setVariables] = useState<
    ISalesInvoicePostMutationVariables | IPurchaseInvoicePostMutationVariables
  >({} as any);
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);

  const [postInvoice] = useMutation<
    ISalesInvoicePostMutationResult | IPurchaseInvoicePostMutationResult,
    ISalesInvoicePostMutationVariables | IPurchaseInvoicePostMutationVariables
  >(postMutation, {
    onError: (data) =>
      handleComponentQueryError(data, {
        dispatch,
        entity: connectedForm.entity,
        name: 'Unable to post invoice',
        customAlertHandler: (_: unknown, meta: any, title: string, message: string) => {
          setPostInProgress(false);
          if (meta.confirmationReference) {
            setPostConfirmationState({
              meta,
              title,
              message,
            });
            return;
          }
          //If no custom handling, call the default handling.
          alertAction({ dispatch, entity: connectedForm?.entity }, meta, title, message);
        },
      }),
  });

  useEffect(() => {
    if (!variables?.input) return;
    postInvoice({
      variables,
    }).then(handlePostResult);
  }, [variables]);

  /**
   * Post an invoice using the response from an invoice save mutation.
   * @param result - Response from invoice save.
   */
  const handleSaveResult = (result: ISalesInvoice) => {
    const savedInvoice = normalizeVariables<ISalesInvoice, ISalesInvoiceInput>(result);
    if (hasValidationErrors({ ...connectedForm, dispatch })) return;
    if (type === 'Sales') {
      const salesInvoicePostMutationVariables: ISalesInvoicePostMutationVariables = {
        input: {
          clientMutationId: getClientMutationId(),
          salesInvoice: savedInvoice,
        },
      };
      setVariables(salesInvoicePostMutationVariables);
    } else {
      const purchaseInvoicePostMutationVariables: IPurchaseInvoicePostMutationVariables = {
        input: {
          clientMutationId: getClientMutationId(),
          id: savedInvoice.id,
        },
      };
      setVariables(purchaseInvoicePostMutationVariables);
    }
  };

  const handlePostResult = (
    response: FetchResult<ISalesInvoicePostMutationResult | IPurchaseInvoicePostMutationResult>
  ) => {
    setPostInProgress(false);
    const result =
      type === 'Sales'
        ? (response?.data as Maybe<ISalesInvoicePostMutationResult>)?.salesInvoicePost?.result
        : (response?.data as Maybe<IPurchaseInvoicePostMutationResult>)?.purchaseInvoicePost?.result;

    if (result?.entityMetaData) {
      dispatch(
        serviceActions.genericAlert(
          `${type} Invoice`,
          `${type} invoice posting completed successfully`,
          AlertType.Success
        )
      );

      if (type === 'Purchase') {
        Analytics.track(
          !(result as IPurchaseInvoice)?.purchaseOrderMatching
            ? 'Purchase Invoice Posted'
            : 'Purchase Order Matching Invoice Posted',
          {
            numberOfLines: result?.invoiceLines?.length,
            numberOfMatchedLines: (result as IPurchaseInvoice)?.invoiceLines?.reduce((count, invoiceLine) => {
              return count + (invoiceLine?.matchedLines?.length || 0);
            }, 0),
          }
        );
        // Return match grid to PO selection slide
        setPOMatchSlideIndex?.(0);
      }

      if (type === 'Sales') {
        const metaActions = (result as ISalesInvoice).entityMetaData!.actions;

        const shouldPrint = false;
        if (metaActions) {
          const print = metaActions.find((action) => action!.actionType === 'PRINT_SALES_INVOICE');
          if (print) {
            dispatch(change(connectedForm.entity?.type, false, result));
            return;
          }
        }
        if (!shouldPrint) {
          connectedForm.afterSubmit?.();
        }
      } else {
        connectedForm.afterSubmit?.();
      }
    }
  };

  const [performSave, saveConfirmationPrompt] = usePerformSave(handleSaveResult, () => setPostInProgress(false));
  const postConfirmationPrompt = usePostConfirmationPrompt(
    postConfirmationState,
    setPostConfirmationState,
    variables,
    setVariables,
    type === 'Sales' ? 'input.salesInvoice' : 'input.purchaseInvoice'
  );

  const performSaveWithoutAfterSubmit = () => performSave({ submitMeta: { suppressAfterSubmit: true } });

  const postButtonProps = useSelectButtonProps(POST_OPTIONS, performSaveWithoutAfterSubmit, postInProgress, 500);

  const result: [IFormAction<SalesInvoiceFormAction | PurchaseInvoiceFormAction>, ReactElement] = [
    {
      key: 'postInvoice',
      text: 'Post Invoice',
      onClick: () => {
        setPostInProgress(true);
        performSaveWithoutAfterSubmit();
      },
      individualButton: true,
      condition: canBePosted,
      actionReference: type === 'Sales' ? SalesInvoiceFormAction.PostInvoice : PurchaseInvoiceFormAction.PostInvoice,
      isPrimary: true,
      buttonProps: postButtonProps,
      'data-testid': 'action-post-invoice-button',
    },
    <>
      {saveConfirmationPrompt}
      {postConfirmationPrompt}
    </>,
  ];
  return result;
}
